// Вкладка «Избранное»: полка сервера по НАШЕМУ набору (параметрами), вкладки — tabs_order со счётчиками.
// Вид по вкладке (макет 1073:19438, HQ-1057): матчи — группами по дням, команды/турниры/игроки — плитками 2 колонки.
// Пусто — серверный empty_state; при рекомендациях — компактный призыв empty_prompt над ними (HQ-1467); рекомендации — строки со звездой (добавить в один тап).
// Подписи вкладок и секций (items/past/recommendations) — block_titles.favorites (§2.9 п.1), иначе свои.
// Лупа шапки — режим поиска на месте по виду вкладки (HQ-1058, FavSearch), а не общий поиск.
import { lazy, type ReactNode, Suspense, useCallback, useEffect, useState } from 'react';
import { type InfiniteData, type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBlockTitle } from '../api/blockTitles';
import type { FavoritesScreen as ShelfPage, LeagueCard, PlayerCard } from '../api/favoritesTypes';
import type { TeamCard } from '../api/homeTypes';
import { useFavoritesShelf } from '../api/queries';
import type { MatchRow } from '../api/types';
import { t, type StringKey } from '../i18n/i18n';
import { favoriteParams, type FavType, useFavoritesState } from '../session/favorites';
import { useCenteredHeader } from '../ui/AppBar';
import { ServerTabStrip } from '../ui/ServerTabStrip';
import { useSearchOverride } from '../ui/searchOverride';
import { EmptyStateView, ErrorView, Loading, QueryFallback } from '../ui/States';
import { FavEmptyPrompt } from './favorites/FavEmptyPrompt';
import { MatchDays, RecommendationRow, SectionHeading } from './favorites/FavSections';
import { restoredFavSearch, searchKindOf, withoutFavSearch } from './favorites/favSearchModel';
import { LeagueTile, PlayerTile, TeamTile, TileGrid } from './favorites/FavTiles';
import sections from './favorites/FavSections.module.css';
import blocks from './match/Blocks.module.css';
import styles from './FavoritesScreen.module.css';

// режим поиска (HQ-1058) — только по лупе, не на первом кадре: своим куском (HQ-1993, запас главного чанка). Не скачался —
// следующий вызов пробует снова (упавший промис не запоминаем; lazy зовёт загрузчик при первом показе)
let favSearchChunk: Promise<typeof import('./favorites/FavSearch')> | undefined;
function loadFavSearch() {
  favSearchChunk ??= import('./favorites/FavSearch').catch((error: unknown) => { favSearchChunk = undefined; throw error; });
  return favSearchChunk;
}
const FavSearch = lazy(() => loadFavSearch().then((m) => ({ default: m.FavSearch })));

const TAB_TITLE: Record<string, StringKey> = { matches: 'mc_matches', teams: 'mc_search_teams', leagues: 'mc_search_leagues', players: 'mc_search_players' };
const TAB_TYPE: Record<string, FavType> = { matches: 'match', teams: 'team', leagues: 'league', players: 'player' };

/** Вкладка полки — state.tab ТЕКУЩЕЙ записи истории (replace, HQ-1736): после «Назад» экран монтируется заново и
 *  вкладка возвращается из записи; новой записи смена не добавляет. */
function useFavTab(): { tab: string | undefined; setTab: (key: string) => void } {
  const location = useLocation();
  const navigate = useNavigate();
  const tab = (location.state as { tab?: string } | null)?.tab;
  const setTab = (key: string) => navigate(location.pathname + location.search, { replace: true, state: { ...(location.state as object | null), tab: key } });
  return { tab, setTab };
}

/** Полоса вкладок по tabs_order полки; полки нет (набор аккаунта грузится или не пришёл, нет сети без кэша) — порядок
 *  TAB_TITLE: полоса видна во всех ветках экрана (Figma 1073:23495 / 1073:23619, HQ-1887).
 *  Подписи без чисел, как в макете (HQ-1273): «Matches · Teams · Leagues · Players» в любом состоянии. */
function FavTabs({ page, active }: { page?: ShelfPage; active?: string }) {
  const { tab, setTab } = useFavTab();
  const title = useBlockTitle('favorites');
  const keys = page ? page.tabs_order.filter((k) => TAB_TITLE[k]) : Object.keys(TAB_TITLE);
  const tabs = keys.map((k) => ({ key: k, label: title(k, t(TAB_TITLE[k]!)) }));
  return <div className={sections.tabs}><ServerTabStrip tabs={tabs} active={active ?? tab ?? page?.tab ?? keys[0]!} onSelect={setTab} /></div>;
}

/** Экран: полоса вкладок на --surface, под ней Middle (у «Матчей» — свои поля и зазор). fill — заглушка (нет сети, ошибка,
 *  загрузка) на весь Middle до нижней панели, по центру (Figma 1073:23495 / 1073:23619). */
function Frame({ tabs, matches = false, fill = false, children }: { tabs: ReactNode; matches?: boolean; fill?: boolean; children: ReactNode }) {
  return (
    <div className={fill ? `${styles.screen} ${styles.fill}` : styles.screen}>
      {tabs}
      <div className={matches ? `${styles.page} ${styles.matches}` : styles.page}>{children}</div>
    </div>
  );
}

function EntityTile({ type, card }: { type: FavType; card: TeamCard | LeagueCard | PlayerCard }) {
  if (type === 'team') return <TeamTile card={card as TeamCard} />;
  if (type === 'league') return <LeagueTile card={card as LeagueCard} />;
  return <PlayerTile card={card as PlayerCard} />;
}

/** Последняя полученная полка этого экрана (любой вкладки) из кэша запросов. */
function knownShelf(queryClient: QueryClient, path: string): ShelfPage | undefined {
  const done = queryClient.getQueryCache().findAll({ queryKey: ['favorites-shelf'] })
    .filter((q) => q.state.data !== undefined && String(q.queryKey[1]).startsWith(path))
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)[0];
  return (done?.state.data as InfiniteData<ShelfPage> | undefined)?.pages[0];
}

/** Подпись «Ещё» прошлых матчей: идёт загрузка — «…», сбой следующей страницы — «Retry» (жать — тот же fetchNextPage). */
export function loadMoreLabel(isFetching: boolean, isError: boolean): string {
  return isFetching ? '…' : isError ? t('mc_retry') : t('mc_load_more');
}

/** Что рисовать, пока решается набор аккаунта (HQ-1795): полка идёт параметрами НАШЕГО набора, поэтому с сессией
 *  до ответа GET account/favorites/ — скелетон без запроса полки (иначе полка с пустыми параметрами и «Recommendations»),
 *  набор не пришёл — ошибка с «Retry». Гость без сессии набора не ждёт: полка сразу (пусто + рекомендации). */
export function favoritesGate({ session, loaded, failed }: { session: boolean; loaded: boolean; failed?: boolean }): 'pending' | 'failed' | 'ready' {
  if (!session || loaded) return 'ready';
  return failed ? 'failed' : 'pending';
}

/** Ключ запроса набора аккаунта (src/session/favorites.ts) — «Retry» перезапрашивает именно его. */
const ACCOUNT_FAVORITES_KEY = ['account-favorites'];

/** «Retry» несгруженного набора: тот же запрос заново; пришёл ответ — экран рисуется по обычным правилам. */
export function retryAccountFavorites(queryClient: QueryClient): Promise<void> {
  return queryClient.refetchQueries({ queryKey: ACCOUNT_FAVORITES_KEY });
}

/** Snackbar «нет интернета» Избранного — на полях 8 (Figma 1073:23619: 8 · 377 · 8 на 393), метка для OfflineBanner (HQ-2213 v3). */
const GUTTER_8 = <span hidden data-offline-gutter="8" />;

export function FavoritesScreen({ path }: { path: string }) {
  return <><FavoritesBody path={path} />{GUTTER_8}</>; // метка после экрана: :has не зависит от места
}

function FavoritesBody({ path }: { path: string }) {
  useCenteredHeader();
  const state = useFavoritesState();
  const queryClient = useQueryClient();
  const gate = favoritesGate(state);
  // запрос набора на паузе без сети — «нет сети» с «Retry», а не вечный скелетон (HQ-1855, как главная HQ-1797)
  if (gate === 'pending') return <Frame tabs={<FavTabs />} fill><Loading query={state.query} /></Frame>;
  if (gate === 'failed') {
    return (
      <Frame tabs={<FavTabs />} fill>
        <ErrorView error={queryClient.getQueryState(ACCOUNT_FAVORITES_KEY)?.error} onRetry={() => void retryAccountFavorites(queryClient)} />
      </Frame>
    );
  }
  return <FavoritesShelf path={path} />;
}

/** Полка по пришедшему набору (или гостевая). */
function FavoritesShelf({ path }: { path: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { tab } = useFavTab();
  const { set } = useFavoritesState();
  const title = useBlockTitle('favorites');
  const shelf = useFavoritesShelf(path, tab, favoriteParams(set));
  const queryClient = useQueryClient();
  // режим поиска: endpoint — адрес поиска из лупы серверной шапки, как есть; «Назад» с сущности из поиска — поиск
  // из state записи (HQ-1796), текст — начальный в поле
  const [search, setSearch] = useState<{ endpoint?: string; text?: string } | null>(() => restoredFavSearch(location.state));
  useSearchOverride(useCallback((endpoint: string | undefined) => setSearch({ endpoint }), []));
  // кусок поиска — заранее, после показа полки: лупа без сети открывает поиск, а не ошибку загрузки
  useEffect(() => { loadFavSearch().catch(() => {}); }, []);
  // «Cancel» убирает поиск и из записи истории, иначе следующий возврат открыл бы его снова
  const { pathname, search: query, state: entryState } = location;
  const closeSearch = useCallback(() => {
    setSearch(null);
    navigate(pathname + query, { replace: true, state: withoutFavSearch(entryState) });
  }, [navigate, pathname, query, entryState]);
  if (search) {
    return <Suspense fallback={null}>
      <FavSearch kind={searchKindOf(shelf.data?.pages[0]?.tab ?? tab)} endpoint={search.endpoint} initialText={search.text} onClose={closeSearch} />
    </Suspense>;
  }
  if (!shelf.data) {
    // вкладка без ответа (нет сети, 5xx): полоса вкладок последней полученной полки (нет её — TAB_TITLE), заглушка под ней
    // (HQ-1060, HQ-1887)
    const known = knownShelf(queryClient, path);
    return <Frame tabs={<FavTabs page={known} />} fill><QueryFallback query={shelf} /></Frame>;
  }
  const first = shelf.data.pages[0]!;
  const type = TAB_TYPE[first.tab] ?? 'match';
  const past = shelf.data.pages.flatMap((p) => p.past ?? []);
  // Снятая звездой сущность уходит сразу (наш набор), не дожидаясь нового ответа полки
  const items = (first.items ?? []).filter((card) => type === 'match' || set[type].includes(card.id));
  const recType: FavType = first.tab === 'leagues' ? 'league' : first.tab === 'players' ? 'player' : 'team';
  const empty = !items.length && !past.length;
  return (
    <Frame tabs={<FavTabs page={first} active={first.tab} />} matches={first.tab === 'matches'}>
      {first.tab === 'matches' ? (
        <>
          {shelf.hasNextPage && (
            <button className={blocks.link} disabled={shelf.isFetchingNextPage} onClick={() => void shelf.fetchNextPage()}>
              {loadMoreLabel(shelf.isFetchingNextPage, shelf.isFetchNextPageError)}
            </button>
          )}
          <MatchDays matches={[...past, ...(items as MatchRow[])]} />
        </>
      ) : items.length > 0 && (
        <div className={styles.block}><TileGrid>{items.map((card) => <EntityTile key={card.id} type={type} card={card as TeamCard} />)}</TileGrid></div>
      )}
      {first.empty_state && empty && !first.recommendations?.length && (
        <EmptyStateView state={first.empty_state} onReload={() => void shelf.refetch()} />
      )}
      {first.empty_prompt && empty && first.recommendations && first.recommendations.length > 0 && (
        <div className={styles.block}><FavEmptyPrompt prompt={first.empty_prompt} /></div>
      )}
      {first.recommendations && first.recommendations.length > 0 && (
        <section className={styles.recs}>
          <SectionHeading>{title('recommendations', t('mc_fav_recommendations'))}</SectionHeading>
          {first.recommendations.map((card) => <RecommendationRow key={`r${card.id}`} type={recType} card={card} />)}
        </section>
      )}
    </Frame>
  );
}
